// src/screens/admin/GeneradorImagenesPanel.jsx
// Sección "🖼️ Generador de Imágenes" del panel de administrador (MoreScreen.jsx).
// Dos pestañas — "Mis Servicios" y "Ofertas Externas" — que comparten el
// mismo motor de generación (fondo con IA + tarjetas de texto + pantalla de
// revisión + compartir) pero difieren en el origen de los datos.
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { toast } from '../../components/UI';
import { getRates } from '../../services/authService';
import { COMBOS, TRANSLATIONS, DOCUMENTOS_CUBA, DOCUMENTOS_CUBA_PRICES } from '../CubaScreen';
import {
  FORMATS, misServiciosFolder, ofertasExternasFolder, listTemplates, uploadTemplate, deleteTemplate,
  fileToBase64Jpeg, extractOfferFromImage, extractOfferFromText, applyMargin, OFFER_MARGIN_PCT,
  buildVuelosFields, guessDestinationFromFreeText, buildBackgroundPrompt, generateAiBackgroundUrl,
  compositeCardDesign, shareGeneratedImages,
} from '../../services/imageGenService';

const SERVICES = [
  { id: 'remesas',         label: 'Remesas',            emoji: '💸' },
  { id: 'cambio_divisas',  label: 'Cambio de Divisas',  emoji: '💱' },
  { id: 'recargas',        label: 'Recargas',           emoji: '📱' },
  { id: 'traducciones',    label: 'Traducciones',       emoji: '📄' },
  { id: 'documentos_cuba', label: 'Documentos de Cuba', emoji: '🏛️' },
  { id: 'vuelos',          label: 'Vuelos',             emoji: '✈️' },
];

// Elección de formato de salida como 3 opciones mutuamente excluyentes (no
// un toggle de "agregar/quitar") -- así "solo cuadrado" y "solo vertical"
// son elecciones reales y compartir nunca manda un formato no elegido.
const FORMAT_CHOICES = [
  { id: 'square', label: 'Cuadrado', formats: ['square'] },
  { id: 'vertical', label: 'Vertical', formats: ['vertical'] },
  { id: 'both', label: 'Ambos', formats: ['square', 'vertical'] },
];

const EMPTY_BACKGROUND = { source: 'ai', url: null, path: null };

// ── Biblioteca de fondos propios (galería + subir + borrar) ───────────
// Alternativa manual al fondo generado con IA.
function TemplateLibrary({ folder, selectedPath, onSelect }) {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    setLoading(true);
    listTemplates(folder).then((list) => { setTemplates(list); setLoading(false); });
  }, [folder]);

  const handleUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const tpl = await uploadTemplate(folder, file);
      setTemplates((list) => [tpl, ...list]);
      onSelect(tpl);
      toast('Fondo subido ✅');
    } catch { toast('Error al subir el fondo.', 'error'); }
    setUploading(false);
  };

  const handleDelete = async (e, path) => {
    e.stopPropagation();
    if (!window.confirm('¿Eliminar este fondo?')) return;
    try {
      await deleteTemplate(path);
      setTemplates((list) => list.filter((t) => t.path !== path));
      if (selectedPath === path) onSelect(null);
      toast('Fondo eliminado ✅');
    } catch { toast('Error al eliminar.', 'error'); }
  };

  return (
    <div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginBottom: 10 }}>
        {loading && <p style={{ color: 'var(--muted)', fontSize: 13 }}>Cargando fondos propios...</p>}
        {!loading && templates.map((t) => (
          <div key={t.path} onClick={() => onSelect(t)} style={{
            position: 'relative', width: 84, height: 84, borderRadius: 10, overflow: 'hidden', cursor: 'pointer',
            border: selectedPath === t.path ? '3px solid var(--orange)' : '1px solid var(--navy-border)',
          }}>
            <img src={t.url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            <button onClick={(e) => handleDelete(e, t.path)} style={{
              position: 'absolute', top: 2, right: 2, background: 'rgba(0,0,0,0.6)', color: 'white',
              borderRadius: 6, width: 20, height: 20, fontSize: 12, lineHeight: '20px', padding: 0,
            }}>✕</button>
          </div>
        ))}
        <button type="button" disabled={uploading} onClick={() => fileRef.current?.click()} style={{
          width: 84, height: 84, borderRadius: 10, border: '1px dashed var(--navy-border)',
          background: 'var(--navy-card)', color: 'var(--muted)', fontSize: 11, fontWeight: 600,
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 4,
          opacity: uploading ? 0.6 : 1,
        }}>
          {uploading ? <span className="spinner" /> : <><span style={{ fontSize: 20 }}>＋</span>Subir</>}
        </button>
        <input ref={fileRef} type="file" accept="image/*" style={{ display: 'none' }}
          onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; handleUpload(f); }} />
      </div>
      {!loading && templates.length === 0 && (
        <p style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 8 }}>Aún no subiste ningún fondo propio.</p>
      )}
    </div>
  );
}

// ── Paso de fondo — generación con IA (Pollinations) + alternativa manual ──
function BackgroundStep({ folder, background, bgLoading, onRegenerate, onSelectManual, onUseSimple, onBack, onNext }) {
  const [showLibrary, setShowLibrary] = useState(false);
  const hasPhoto = !bgLoading && background.url;

  return (
    <div>
      {onBack && <button onClick={onBack} style={{ background: 'none', color: 'var(--muted)', fontSize: 13, marginBottom: 14 }}>← Volver</button>}

      <p className="section-label">FONDO GENERADO CON IA</p>
      <div style={{
        width: '100%', maxWidth: 220, aspectRatio: '9 / 16', margin: '0 auto 14px',
        borderRadius: 14, overflow: 'hidden', background: 'var(--navy-card)', border: '1px solid var(--navy-border)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        {bgLoading
          ? <span className="spinner" />
          : hasPhoto
            ? <img src={background.url} alt="Fondo generado" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            : <span style={{ fontSize: 12, color: 'var(--muted)', padding: '0 16px', textAlign: 'center' }}>Diseño simple (sin foto)</span>}
      </div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
        <button className="btn-outline" style={{ flex: 1 }} disabled={bgLoading} onClick={onRegenerate}>🔄 Generar otro fondo</button>
        <button className="btn-outline" style={{ flex: 1 }} onClick={onUseSimple}>🎨 Diseño simple</button>
      </div>

      <button onClick={() => setShowLibrary((v) => !v)} style={{ background: 'none', color: 'var(--orange)', fontSize: 12, fontWeight: 700, marginBottom: 12 }}>
        {showLibrary ? '▲ Ocultar' : '▼'} 📷 Subir/usar mi propio fondo
      </button>
      {showLibrary && (
        <TemplateLibrary folder={folder} selectedPath={background.source === 'template' ? background.path : null} onSelect={onSelectManual} />
      )}

      <button className="btn-primary" style={{ marginTop: 16 }} onClick={onNext}>Generar imagen →</button>
    </div>
  );
}

// ── Pantalla de revisión — el motor compartido por ambas pestañas ────
function ReviewScreen({ backgroundUrl, bgLoading, onRegenerateBackground, fields, onFieldsChange, onBack, extraActions, title, emoji }) {
  const [formatChoice, setFormatChoice] = useState('square');
  const [previewByFormat, setPreviewByFormat] = useState({});
  const [sharing, setSharing] = useState(false);
  const formats = FORMAT_CHOICES.find((c) => c.id === formatChoice).formats;

  useEffect(() => { setPreviewByFormat({}); }, [backgroundUrl]);

  useEffect(() => {
    if (fields.length === 0 || bgLoading) return;
    let cancelled = false;
    (async () => {
      for (const format of formats) {
        const blob = await compositeCardDesign({ fields, format, title, emoji, backgroundUrl });
        if (!cancelled) setPreviewByFormat((p) => ({ ...p, [format]: URL.createObjectURL(blob) }));
      }
    })();
    return () => { cancelled = true; };
  }, [backgroundUrl, bgLoading, fields, formats, title, emoji]);

  const handleFieldChange = (key, text) => {
    onFieldsChange(fields.map((f) => (f.key === key ? { ...f, text } : f)));
  };

  const handleShare = async () => {
    const ready = formats.filter((f) => previewByFormat[f]);
    if (ready.length === 0) return toast('Espera a que la imagen termine de generarse.', 'error');
    setSharing(true);
    try {
      const blobsByFormat = {};
      for (const format of ready) {
        blobsByFormat[format] = await compositeCardDesign({ fields, format, title, emoji, backgroundUrl });
      }
      const shared = await shareGeneratedImages(blobsByFormat);
      if (!shared) toast('Tu navegador no admite compartir imágenes directamente desde aquí.', 'error');
    } catch (err) {
      if (err?.name !== 'AbortError') toast('Error al compartir.', 'error');
    }
    setSharing(false);
  };

  return (
    <div>
      {onBack && <button onClick={onBack} style={{ background: 'none', color: 'var(--muted)', fontSize: 13, marginBottom: 14 }}>← Volver</button>}

      {extraActions}

      <p className="section-label">VISTA PREVIA</p>
      {onRegenerateBackground && (
        <button onClick={onRegenerateBackground} disabled={bgLoading} style={{
          background: 'var(--navy-card)', border: '1px solid var(--navy-border)', color: 'var(--white)',
          fontWeight: 700, fontSize: 12, borderRadius: 8, padding: '8px 14px', marginBottom: 10,
        }}>🔄 Generar otro fondo</button>
      )}
      <div style={{ display: 'flex', gap: 10, overflowX: 'auto', marginBottom: 16 }}>
        {formats.map((format) => (
          <div key={format} style={{ flex: '0 0 auto', textAlign: 'center' }}>
            <div style={{
              width: format === 'square' ? 180 : 130, height: 180,
              borderRadius: 12, overflow: 'hidden', background: 'var(--navy-card)',
              border: '1px solid var(--navy-border)', display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              {bgLoading || !previewByFormat[format]
                ? <span className="spinner" />
                : <img src={previewByFormat[format]} alt={format} style={{ width: '100%', height: '100%', objectFit: 'contain' }} />}
            </div>
            <p style={{ fontSize: 10, color: 'var(--muted)', marginTop: 4 }}>{FORMATS[format].label}</p>
          </div>
        ))}
      </div>

      <p className="section-label">TEXTOS (editables)</p>
      {fields.map((f) => (
        <div key={f.key} style={{ marginBottom: 10 }}>
          <label>{f.label}</label>
          <input value={f.text} onChange={(e) => handleFieldChange(f.key, e.target.value)} />
        </div>
      ))}

      <p className="section-label">FORMATO DE SALIDA</p>
      <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
        {FORMAT_CHOICES.map((c) => (
          <button key={c.id} onClick={() => setFormatChoice(c.id)} style={{
            flex: 1, padding: '10px 0', borderRadius: 8, fontSize: 12, fontWeight: 700,
            background: formatChoice === c.id ? 'var(--orange)' : 'var(--navy-card)',
            color: formatChoice === c.id ? 'white' : 'var(--muted)',
            border: `1px solid ${formatChoice === c.id ? 'var(--orange)' : 'var(--navy-border)'}`,
          }}>{c.label}</button>
        ))}
      </div>

      <button className="btn-primary" onClick={handleShare} disabled={sharing || bgLoading || formats.some((f) => !previewByFormat[f])}>
        {sharing ? <span className="spinner" /> : '📤 Compartir'}
      </button>
    </div>
  );
}

// ── Pestaña "Mis Servicios" ────────────────────────────────────────────
function MisServiciosTab() {
  const [step, setStep] = useState('select');
  const [service, setService] = useState(null);
  const [rates, setRates] = useState(null);
  const [selectedCombos, setSelectedCombos] = useState([]);
  const [selectedTranslations, setSelectedTranslations] = useState([]);
  const [selectedDocs, setSelectedDocs] = useState([]);
  const [vuelosText, setVuelosText] = useState('');
  const [fields, setFields] = useState([]);
  const [background, setBackground] = useState(EMPTY_BACKGROUND);
  const [bgLoading, setBgLoading] = useState(false);

  useEffect(() => { getRates().then(setRates); }, []);

  const handleSelectService = (id) => {
    setService(id);
    if (id === 'recargas') setSelectedCombos(COMBOS.map((_, i) => i));
    if (id === 'traducciones') setSelectedTranslations(TRANSLATIONS.map((_, i) => i));
    if (id === 'documentos_cuba') setSelectedDocs(DOCUMENTOS_CUBA.map((_, i) => i));
  };

  const buildFields = () => {
    if (service === 'remesas') {
      if (!rates) return [];
      return [
        { key: 'rate_low', label: 'Menos de R$100', text: rates.low > 0 ? `${rates.low.toFixed(2)} CUP por BRL` : '' },
        { key: 'rate_high', label: 'R$100 o más', text: rates.high > 0 ? `${rates.high.toFixed(2)} CUP por BRL` : '' },
      ];
    }
    if (service === 'cambio_divisas') {
      if (!rates) return [];
      return [
        { key: 'rate_brl_usd', label: 'BRL → USD', text: rates.brlToUsd > 0 ? `${rates.brlToUsd.toFixed(2)} BRL por 1 USD` : '' },
        { key: 'rate_usd_brl', label: 'USD → BRL', text: rates.usdToBrl > 0 ? `${rates.usdToBrl.toFixed(2)} USD por 1 BRL` : '' },
      ];
    }
    if (service === 'recargas') {
      return selectedCombos.map((i) => {
        const c = COMBOS[i];
        return {
          key: `combo_${i}`, label: c.name, text: `${c.emoji} ${c.name} — R$${c.price}`,
          details: [{ icon: '📶', text: c.data }, { icon: '📞', text: c.min }, { icon: '✉️', text: `${c.sms} SMS` }],
        };
      });
    }
    if (service === 'traducciones') {
      return selectedTranslations.map((i) => {
        const t = TRANSLATIONS[i];
        return { key: `trad_${i}`, label: t.name, text: `${t.name} — R$${t.price}` };
      });
    }
    if (service === 'documentos_cuba') {
      const base = [
        { key: 'minjus', label: 'MINJUS', text: `MINJUS — R$${DOCUMENTOS_CUBA_PRICES.minjus}` },
        { key: 'embajada', label: 'Embajada', text: `Embajada de Brasil — R$${DOCUMENTOS_CUBA_PRICES.embajada}` },
      ];
      const docs = selectedDocs.map((i) => {
        const d = DOCUMENTOS_CUBA[i];
        return { key: `doc_${i}`, label: d.name, text: d.nota ? `${d.name} (${d.nota})` : d.name };
      });
      return [...base, ...docs];
    }
    if (service === 'vuelos') return buildVuelosFields(vuelosText);
    return [];
  };

  const serviceMeta = SERVICES.find((s) => s.id === service);
  const vuelosDestination = service === 'vuelos' ? guessDestinationFromFreeText(vuelosText) : null;
  const prompt = service ? buildBackgroundPrompt(service, { destination: vuelosDestination }) : null;

  const regenerateBackground = useCallback(() => {
    if (!prompt) return;
    setBgLoading(true);
    const candidate = generateAiBackgroundUrl(prompt);
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => { setBackground({ source: 'ai', url: candidate, path: null }); setBgLoading(false); };
    img.onerror = () => { setBgLoading(false); toast('No se pudo generar el fondo con IA, intenta de nuevo.', 'error'); };
    img.src = candidate;
  }, [prompt]);

  useEffect(() => {
    if (step === 'background') regenerateBackground();
  }, [step]);

  const goToBackground = () => {
    const f = buildFields();
    if (f.length === 0) return toast('Selecciona o completa al menos un dato para continuar.', 'error');
    setFields(f);
    setStep('background');
  };

  if (step === 'review') {
    return (
      <ReviewScreen
        backgroundUrl={background.url} bgLoading={bgLoading} onRegenerateBackground={regenerateBackground}
        fields={fields} onFieldsChange={setFields} onBack={() => setStep('background')}
        title={serviceMeta?.label} emoji={serviceMeta?.emoji}
      />
    );
  }

  if (step === 'background') {
    return (
      <BackgroundStep
        folder={misServiciosFolder(service)} background={background} bgLoading={bgLoading}
        onRegenerate={regenerateBackground}
        onSelectManual={(t) => setBackground(t ? { source: 'template', url: t.url, path: t.path } : { source: 'none', url: null, path: null })}
        onUseSimple={() => setBackground({ source: 'none', url: null, path: null })}
        onBack={() => setStep('select')}
        onNext={() => setStep('review')}
      />
    );
  }

  return (
    <div>
      <p className="section-label">ELIGE UN SERVICIO</p>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 20 }}>
        {SERVICES.map((s) => (
          <button key={s.id} onClick={() => handleSelectService(s.id)} style={{
            padding: '16px 10px', borderRadius: 12, textAlign: 'center',
            background: service === s.id ? 'rgba(255,107,0,0.12)' : 'var(--navy-card)',
            border: `1px solid ${service === s.id ? 'var(--orange)' : 'var(--navy-border)'}`,
          }}>
            <div style={{ fontSize: 26, marginBottom: 6 }}>{s.emoji}</div>
            <p style={{ fontSize: 12, fontWeight: 700, color: 'var(--white)' }}>{s.label}</p>
          </button>
        ))}
      </div>

      {service === 'remesas' && (
        <div style={{ marginBottom: 20 }}>
          <p className="section-label">TASA DEL DÍA — REMESAS (BRL → CUP, autocompletada)</p>
          {!rates ? <p style={{ color: 'var(--muted)', fontSize: 13 }}>Cargando tasas...</p> : (
            <div style={{ background: 'var(--navy-card)', border: '1px solid var(--navy-border)', borderRadius: 12, padding: 14 }}>
              <p style={{ fontSize: 13, color: 'var(--white)', marginBottom: 6 }}>Menos de R$100: <strong>{rates.low > 0 ? `${rates.low.toFixed(2)} CUP` : '—'}</strong> por BRL</p>
              <p style={{ fontSize: 13, color: 'var(--white)' }}>R$100 o más: <strong>{rates.high > 0 ? `${rates.high.toFixed(2)} CUP` : '—'}</strong> por BRL</p>
            </div>
          )}
        </div>
      )}

      {service === 'cambio_divisas' && (
        <div style={{ marginBottom: 20 }}>
          <p className="section-label">TASA DEL DÍA — CAMBIO DE DIVISAS (autocompletada)</p>
          {!rates ? <p style={{ color: 'var(--muted)', fontSize: 13 }}>Cargando tasas...</p> : (
            <div style={{ background: 'var(--navy-card)', border: '1px solid var(--navy-border)', borderRadius: 12, padding: 14 }}>
              <p style={{ fontSize: 13, color: 'var(--white)', marginBottom: 6 }}>🇧🇷 BRL → USD 🇺🇸: <strong>{rates.brlToUsd > 0 ? rates.brlToUsd.toFixed(2) : '—'}</strong></p>
              <p style={{ fontSize: 13, color: 'var(--white)' }}>🇺🇸 USD → BRL 🇧🇷: <strong>{rates.usdToBrl > 0 ? rates.usdToBrl.toFixed(2) : '—'}</strong></p>
            </div>
          )}
        </div>
      )}

      {service === 'recargas' && (
        <div style={{ marginBottom: 20 }}>
          <p className="section-label">COMBOS A MOSTRAR</p>
          {COMBOS.map((c, i) => (
            <div key={c.name} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
              <input type="checkbox" checked={selectedCombos.includes(i)}
                onChange={(e) => setSelectedCombos((sel) => (e.target.checked ? [...sel, i] : sel.filter((x) => x !== i)))} />
              <span style={{ fontSize: 13, color: 'var(--white)' }}>{c.emoji} {c.name} — R${c.price}</span>
            </div>
          ))}
        </div>
      )}

      {service === 'traducciones' && (
        <div style={{ marginBottom: 20 }}>
          <p className="section-label">DOCUMENTOS A MOSTRAR</p>
          {TRANSLATIONS.map((t, i) => (
            <div key={t.name} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
              <input type="checkbox" checked={selectedTranslations.includes(i)}
                onChange={(e) => setSelectedTranslations((sel) => (e.target.checked ? [...sel, i] : sel.filter((x) => x !== i)))} />
              <span style={{ fontSize: 13, color: 'var(--white)' }}>{t.name} — R${t.price}</span>
            </div>
          ))}
        </div>
      )}

      {service === 'documentos_cuba' && (
        <div style={{ marginBottom: 20 }}>
          <p className="section-label">DOCUMENTOS A MOSTRAR (MINJUS R${DOCUMENTOS_CUBA_PRICES.minjus} y Embajada R${DOCUMENTOS_CUBA_PRICES.embajada} siempre incluidos)</p>
          {DOCUMENTOS_CUBA.map((d, i) => (
            <div key={d.name} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
              <input type="checkbox" checked={selectedDocs.includes(i)}
                onChange={(e) => setSelectedDocs((sel) => (e.target.checked ? [...sel, i] : sel.filter((x) => x !== i)))} />
              <span style={{ fontSize: 13, color: 'var(--white)' }}>{d.name}</span>
            </div>
          ))}
        </div>
      )}

      {service === 'vuelos' && (
        <div style={{ marginBottom: 20 }}>
          <p className="section-label">INFORMACIÓN DEL VUELO (texto libre — aún no hay datos guardados de vuelos)</p>
          <textarea rows={4} value={vuelosText} onChange={(e) => setVuelosText(e.target.value)}
            placeholder="Ej: La Habana ✈ Miami — Cubana de Aviación — Desde $320 — Salidas: Julio 21, 28" />
        </div>
      )}

      {service && <button className="btn-primary" onClick={goToBackground}>Siguiente: generar fondo →</button>}
    </div>
  );
}

// ── Pestaña "Ofertas Externas" ─────────────────────────────────────────
function OfertasExternasTab() {
  const [step, setStep] = useState('input');
  const [mode, setMode] = useState('texto');
  const [pastedText, setPastedText] = useState('');
  const [extracting, setExtracting] = useState(false);
  const [offer, setOffer] = useState(null);
  const [fields, setFields] = useState([]);
  const [background, setBackground] = useState(EMPTY_BACKGROUND);
  const [bgLoading, setBgLoading] = useState(false);
  const fileRef = useRef(null);

  const prompt = offer ? buildBackgroundPrompt('oferta_externa', { destination: offer.destination }) : null;

  const regenerateBackground = useCallback(() => {
    if (!prompt) return;
    setBgLoading(true);
    const candidate = generateAiBackgroundUrl(prompt);
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => { setBackground({ source: 'ai', url: candidate, path: null }); setBgLoading(false); };
    img.onerror = () => { setBgLoading(false); toast('No se pudo generar el fondo con IA, intenta de nuevo.', 'error'); };
    img.src = candidate;
  }, [prompt]);

  useEffect(() => {
    if (step === 'background') regenerateBackground();
  }, [step]);

  const buildFieldsFromOffer = (o) => ([
    { key: 'origin', label: 'Origen', text: o.origin || '' },
    { key: 'destination', label: 'Destino', text: o.destination || '' },
    { key: 'airline', label: 'Aerolínea', text: o.airline || '' },
    { key: 'price', label: 'Precio (con margen)', text: o.originalPrice ? `R$ ${applyMargin(o.originalPrice, o.marginPct).toFixed(2)}` : '' },
    { key: 'dates', label: 'Fechas', text: o.dates || '' },
  ]);

  const finishExtraction = (extracted) => {
    const missing = [];
    if (!extracted.origin) missing.push('origen');
    if (!extracted.destination) missing.push('destino');
    if (!extracted.airline) missing.push('aerolínea');
    if (!extracted.price) missing.push('precio');
    if (!extracted.dates) missing.push('fechas');
    if (missing.length) toast(`No se pudo leer con claridad: ${missing.join(', ')}. Complétalo manualmente.`, 'error');

    const o = {
      origin: extracted.origin, destination: extracted.destination, airline: extracted.airline,
      originalPrice: extracted.price, marginPct: OFFER_MARGIN_PCT, dates: extracted.dates,
    };
    setOffer(o);
    setFields(buildFieldsFromOffer(o));
    setStep('background');
  };

  const handleExtractText = () => {
    if (!pastedText.trim()) return toast('Pega el texto de la oferta primero.', 'error');
    finishExtraction(extractOfferFromText(pastedText));
  };

  const handleUploadOfferImage = async (file) => {
    if (!file) return;
    setExtracting(true);
    try {
      const { base64, mediaType } = await fileToBase64Jpeg(file);
      const extracted = await extractOfferFromImage({ base64, mediaType });
      finishExtraction(extracted);
    } catch { toast('No se pudo leer la imagen.', 'error'); }
    setExtracting(false);
  };

  const handleMarginChange = (marginPct) => {
    setOffer((o) => {
      const next = { ...o, marginPct };
      setFields((fs) => fs.map((f) => (f.key === 'price'
        ? { ...f, text: next.originalPrice ? `R$ ${applyMargin(next.originalPrice, marginPct).toFixed(2)}` : '' }
        : f)));
      return next;
    });
  };

  const handleOriginalPriceChange = (val) => {
    const price = parseFloat(val.replace(',', '.'));
    const safePrice = Number.isFinite(price) ? price : null;
    setOffer((o) => {
      const next = { ...o, originalPrice: safePrice };
      setFields((fs) => fs.map((f) => (f.key === 'price'
        ? { ...f, text: safePrice ? `R$ ${applyMargin(safePrice, next.marginPct).toFixed(2)}` : '' }
        : f)));
      return next;
    });
  };

  if (step === 'input') {
    return (
      <div>
        <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
          {[{ id: 'texto', label: '✍️ Pegar texto' }, { id: 'imagen', label: '🖼️ Subir imagen' }].map((m) => (
            <button key={m.id} onClick={() => setMode(m.id)} style={{
              flex: 1, padding: '10px 0', borderRadius: 8, fontSize: 12, fontWeight: 700,
              background: mode === m.id ? 'var(--orange)' : 'var(--navy-card)',
              color: mode === m.id ? 'white' : 'var(--muted)',
              border: `1px solid ${mode === m.id ? 'var(--orange)' : 'var(--navy-border)'}`,
            }}>{m.label}</button>
          ))}
        </div>

        {mode === 'texto' && (
          <div>
            <p className="section-label">TEXTO DE LA OFERTA</p>
            <textarea rows={6} value={pastedText} onChange={(e) => setPastedText(e.target.value)}
              placeholder="Pega aquí el texto de la oferta tal como llegó al grupo..." style={{ marginBottom: 12 }} />
            <button className="btn-primary" onClick={handleExtractText}>Extraer datos →</button>
          </div>
        )}

        {mode === 'imagen' && (
          <div>
            <p className="section-label">CAPTURA DE LA OFERTA</p>
            <input ref={fileRef} type="file" accept="image/*" style={{ display: 'none' }}
              onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; handleUploadOfferImage(f); }} />
            <button className="btn-primary" disabled={extracting} onClick={() => fileRef.current?.click()}>
              {extracting ? <span className="spinner" /> : '🖼️ Elegir imagen'}
            </button>
          </div>
        )}
      </div>
    );
  }

  if (step === 'background') {
    return (
      <BackgroundStep
        folder={ofertasExternasFolder()} background={background} bgLoading={bgLoading}
        onRegenerate={regenerateBackground}
        onSelectManual={(t) => setBackground(t ? { source: 'template', url: t.url, path: t.path } : { source: 'none', url: null, path: null })}
        onUseSimple={() => setBackground({ source: 'none', url: null, path: null })}
        onBack={() => setStep('input')}
        onNext={() => setStep('review')}
      />
    );
  }

  return (
    <ReviewScreen
      backgroundUrl={background.url} bgLoading={bgLoading} onRegenerateBackground={regenerateBackground}
      fields={fields} onFieldsChange={setFields} onBack={() => setStep('background')}
      title="Oferta de Vuelo" emoji="✈️"
      extraActions={
        <div style={{ marginBottom: 16 }}>
          <p className="section-label">PRECIO Y MARGEN</p>
          <div style={{ display: 'flex', gap: 10 }}>
            <div style={{ flex: 1 }}>
              <label>PRECIO ORIGINAL (R$)</label>
              <input inputMode="decimal" value={offer.originalPrice ?? ''} onChange={(e) => handleOriginalPriceChange(e.target.value)} placeholder="—" />
            </div>
            <div style={{ flex: 1 }}>
              <label>MARGEN (%)</label>
              <input inputMode="decimal" value={offer.marginPct} onChange={(e) => handleMarginChange(parseFloat(e.target.value.replace(',', '.')) || 0)} />
            </div>
          </div>
        </div>
      }
    />
  );
}

// ── Contenedor de la sección ────────────────────────────────────────────
export default function GeneradorImagenesPanel() {
  const [tab, setTab] = useState('mis-servicios');

  return (
    <div>
      <div style={{ display: 'flex', background: 'var(--navy-card)', borderRadius: 10, padding: 4, marginBottom: 20, gap: 4 }}>
        {[{ id: 'mis-servicios', label: 'Mis Servicios' }, { id: 'ofertas-externas', label: 'Ofertas Externas' }].map((t) => (
          <button key={t.id} onClick={() => setTab(t.id)} style={{
            flex: 1, padding: '9px 0', borderRadius: 8, fontSize: 12, fontWeight: 700,
            background: tab === t.id ? 'var(--orange)' : 'transparent',
            color: tab === t.id ? 'white' : 'var(--muted)',
          }}>{t.label}</button>
        ))}
      </div>
      {tab === 'mis-servicios' ? <MisServiciosTab /> : <OfertasExternasTab />}
    </div>
  );
}
